import { UMFormGroup, UMIcon, UMInput, UMR, UMSegmentedControl, UMSelect } from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import { fmt } from '../proposition-c/ui';
import type { PerimeterOption, TerritoryKind } from './types';
import { Card, Row } from './ui';

const KIND_LABEL: Record<TerritoryKind, string> = {
  communes: 'Ensemble de communes',
  commune: 'Commune',
  zone: 'Zone géographique',
};

const KINDS: TerritoryKind[] = ['communes', 'commune', 'zone'];

export interface PerimeterValue {
  name: string;
  kind: TerritoryKind;
  perimeterId: string;
}

export function StepPerimeter({
  value, onChange, perimeters, lineage,
}: {
  value: PerimeterValue;
  onChange: (v: PerimeterValue) => void;
  perimeters: PerimeterOption[];
  lineage: { source: string; version: string; extractedAt: string };
}) {
  const matching = perimeters.filter((p) => p.kind === value.kind);
  const selected = perimeters.find((p) => p.id === value.perimeterId);
  const empty = selected?.buildingCount === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <UMFormGroup label="Nom du projet">
        <UMInput value={value.name} onChange={(name) => onChange({ ...value, name })} ariaLabel="Nom du projet" placeholder="AO Gentilly — réseau de chaleur" />
      </UMFormGroup>

      <UMFormGroup label="Type de territoire">
        <UMSegmentedControl
          options={KINDS.map((k) => KIND_LABEL[k])}
          value={KIND_LABEL[value.kind]}
          onChange={(label) => {
            const kind = KINDS.find((k) => KIND_LABEL[k] === label) ?? 'commune';
            const first = perimeters.find((p) => p.kind === kind);
            onChange({ ...value, kind, perimeterId: first?.id ?? '' });
          }}
        />
      </UMFormGroup>

      <UMFormGroup label="Territoire">
        <UMSelect
          value={value.perimeterId}
          ariaLabel="Territoire"
          onChange={(perimeterId) => onChange({ ...value, perimeterId })}
          options={matching.map((p) => ({ value: p.id, label: p.label }))}
        />
      </UMFormGroup>

      {empty ? (
        <div style={{ display: 'flex', gap: 12, padding: 16, background: PLAN.warningBg, border: `1px solid ${PLAN.warningBorder}`, color: PLAN.warningText, fontSize: 14 }}>
          <UMIcon name="error" size={22} color={PLAN.warningText} />
          <div>
            <strong>Aucun bâtiment dans ce périmètre.</strong> UrbanMap ne connaît aucun bâtiment sur « {selected?.label} ».
            Le projet ne peut pas être créé : choisissez un autre territoire.
          </div>
        </div>
      ) : (
        <Card title="Repris depuis UrbanMap" icon="content_copy">
          <div style={{ fontSize: 12, color: UMR.textSecondary }}>
            Copie figée à la date de création · les évolutions ultérieures d’UrbanMap ne seront pas reportées
          </div>
          <Row label="Bâtiments du périmètre" value={fmt(selected?.buildingCount ?? 0)} />
          <Row label="Identifiants fonctionnels UrbanMap" value="conservés" />
          <Row label="Source de la donnée" value={lineage.source} />
          <Row label="Version du référentiel" value={lineage.version} />
        </Card>
      )}
    </div>
  );
}

export function perimeterValid(v: PerimeterValue, perimeters: PerimeterOption[]): boolean {
  const selected = perimeters.find((p) => p.id === v.perimeterId);
  return Boolean(v.name.trim()) && Boolean(selected) && (selected?.buildingCount ?? 0) > 0;
}
